import {
  ConditionComparator,
  FieldId,
  FieldTypeKind,
  FormFieldCondition,
  FormFieldValue,
} from '@components/form/v2';
import { FieldType } from '@components/text-editors/addons/field-mapping';

export type ConditionDraft = {
  id: string;
  questionId?: string;
  comparator?: ConditionComparator;
  value?: FormFieldValue;
};

const kindToFieldType: Partial<Record<FieldTypeKind, FieldType>> = {
  'input-dropdown': 'select',
  'input-single-choice': 'select',
  'input-multiple-choice': 'multi_select',
  'input-date': 'date',
  'input-number': 'number',
  'input-file': 'file',
  'input-text-small': 'text',
  'input-text-large': 'text',
  'input-email': 'text',
  'input-phone': 'text',
  'input-kennitala': 'text',
};

export const getElementTypeForCondition = (
  condition: ConditionDraft,
  getFieldKind: (id: string) => FieldTypeKind | undefined
): FieldType | undefined => {
  if (!condition.questionId) return undefined;
  const kind = getFieldKind(condition.questionId);
  if (!kind) return undefined;
  return kindToFieldType[kind];
};

export const resetConditionForNewElement = (
  condition: ConditionDraft,
  questionId: string
): ConditionDraft => ({ id: condition.id, questionId });

export const MULTI_COMPARATORS: ReadonlySet<ConditionComparator> = new Set(['any_of', 'none_of']);

export const isMultiComparator = (comparator: ConditionComparator): boolean =>
  MULTI_COMPARATORS.has(comparator);

const VALUELESS_COMPARATORS = new Set<string>(['empty', 'nempty']);

const comparatorRequiresValue = (comparator: ConditionComparator): boolean =>
  !VALUELESS_COMPARATORS.has(comparator);

const hasValue = (value: FormFieldValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

export const canSaveCondition = (condition: ConditionDraft): boolean => {
  if (!condition.questionId) return false;
  if (!condition.comparator) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

export const applyComparatorChange = (
  condition: FormFieldCondition,
  next: ConditionComparator
): FormFieldCondition => {
  if (next === condition.comparator) return condition;
  const wasMulti = isMultiComparator(condition.comparator);
  const willBeMulti = isMultiComparator(next);
  if (wasMulti === willBeMulti) {
    return { ...condition, comparator: next };
  }
  if (willBeMulti) {
    const carry =
      typeof condition.value === 'string' && condition.value.length > 0 ? [condition.value] : [];
    return { ...condition, comparator: next, value: carry };
  }
  const currentArray = Array.isArray(condition.value) ? (condition.value as string[]) : [];
  return { ...condition, comparator: next, value: currentArray[0] ?? '' };
};

export const applyValueChange = (
  condition: FormFieldCondition,
  next: FormFieldValue
): FormFieldCondition => {
  if (!isMultiComparator(condition.comparator)) {
    return { ...condition, value: next };
  }
  const current = Array.isArray(condition.value) ? (condition.value as string[]) : [];
  const nextString = next as string;
  const nextValue = current.includes(nextString)
    ? current.filter((value) => value !== nextString)
    : [...current, nextString];
  return { ...condition, value: nextValue };
};

export const isCompleteCondition = (condition: FormFieldCondition): boolean => {
  if (!condition.questionId || !condition.comparator) return false;
  const valueIsArray = Array.isArray(condition.value);
  const expectArray = isMultiComparator(condition.comparator);
  if (expectArray !== valueIsArray) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

export const getRenderableConditionCount = (conditions: FormFieldCondition[]): number =>
  conditions.length;

export const applyElementChange = (
  condition: FormFieldCondition,
  nextQuestionId: FieldId,
  availableComparators: ReadonlyArray<ConditionComparator>
): FormFieldCondition => {
  const nextComparator = availableComparators.includes(condition.comparator)
    ? condition.comparator
    : availableComparators[0] ?? 'is';
  return {
    source: 'form',
    id: condition.id,
    questionId: nextQuestionId,
    comparator: nextComparator,
    value: isMultiComparator(nextComparator) ? [] : '',
  };
};
